import React, { useEffect, useState } from "react";
import { fetchZAttributes, upSertZAttribute } from "../../../api";
import Select, { ActionMeta, MultiValue, SingleValue } from "react-select";
import CustomModal from "../../widgets/components/CustomModal";
import { CirclePicker } from "react-color";
import { ZAttributes, AttrType } from "../../../types";
import { toast } from 'react-toastify';
import { mergeSelectStyles } from "../../widgets/components/SelectStyles";

export interface OptionType {
  value: string;
  label: string;
  color?: string;
}

interface GenericPickerProps {
  categoryType: AttrType;
  label: string;
  tabIndex?: number;
  onAttributeChange?: (attributeId: string) => void;
  showColor?: boolean;
  selectedAttribute?: string;
}

export function GenericPicker({
  categoryType,
  label,
  tabIndex,
  onAttributeChange,
  showColor = false,
  selectedAttribute,
}: GenericPickerProps) {
  const [attributes, setAttributes] = useState<OptionType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);

  useEffect(() => {
    const getAttributes = async () => {
      try {
        const fetchedAttributes: ZAttributes[] = await fetchZAttributes(categoryType);
        const attributesArray = Array.isArray(fetchedAttributes) ? fetchedAttributes : [fetchedAttributes];
        const options = attributesArray.map(attribute => ({
          value: attribute.attrId,
          label: attribute.attrNm,
          color: showColor ? attribute.attrColor : undefined,
        }));
        setAttributes(options);

        // Set the default selected attribute if provided
        if (selectedAttribute) {
          const defaultOption = options.find(option => option.value === selectedAttribute) || null;
          setSelectedOption(defaultOption);
        }
      } catch (error) {
        console.error(`Error fetching ${label}:`, error);
      }
    };

    getAttributes();
  }, [categoryType, showColor, selectedAttribute]);

  const customStyles = mergeSelectStyles({
    option: (provided: any, { data }: any) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
    }),
    singleValue: (provided: any, { data }: any) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
    }),
  });

  const formatOptionLabel = ({ label, color }: OptionType) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {showColor && color && (
        <span style={{ backgroundColor: color, width: 16, height: 16, display: 'inline-block', marginRight: 8 }}></span>
      )}
      {label}
    </div>
  );

  const handleSelectChange = (selectedOption: SingleValue<OptionType> | MultiValue<OptionType>, _actionMeta: ActionMeta<OptionType>) => {
    const option = selectedOption as SingleValue<OptionType>;
    if (option && option.value === 'add_new') {
      setIsModalOpen(true);
    } else {
      setSelectedOption(option);
     if(onAttributeChange) onAttributeChange(option?.value || '');
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleAddNew = (newOption: OptionType) => {
    setIsModalOpen(false);
    setAttributes(prevAttributes => [...prevAttributes, newOption]);
    setSelectedOption(newOption);
    if(onAttributeChange) onAttributeChange(newOption.value);
  };

  const modifiedOptions = [
    { value: 'add_new', label: 'Add New'.toTranslatedDisplay(), color: showColor ? '' : undefined },
    ...attributes,
  ];

  return (
    <div className="searchable-picker">
      {label && <label className="form-label">{label}</label>}
      <Select
        className="react-select-styled react-select"
        classNamePrefix="react-select"
        options={modifiedOptions}
        formatOptionLabel={formatOptionLabel}
        styles={customStyles}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        placeholder={`Select ${label}`}
        value={selectedOption}
        onChange={handleSelectChange}
        tabIndex={tabIndex}
        isClearable
      />
      <CustomModal
        title={`${'Add New'.toTranslatedDisplay()} ${(label || '').toTranslatedDisplay()}`}
        isVisible={isModalOpen}
        onClose={handleModalClose}
        body={<GenericForm attrType={categoryType} onAddNew={handleAddNew} showColor={showColor} label={label} />}
        id={""}
        onSave={() => {}}
      />
    </div>
  );
}

interface GenericFormProps {
  attrType: AttrType;
  onAddNew: (newOption: OptionType) => void;
  showColor?: boolean;
  label: string;
  existingValue?:ZAttributes
}

export function GenericForm({ attrType, onAddNew, showColor = false,label,existingValue }: GenericFormProps) {
  const [category, setCategory] = useState<ZAttributes>(existingValue

    || new ZAttributes({attrType:attrType})
  )

  useEffect(() => {
   if(existingValue) setCategory(existingValue)
  },[existingValue?.attrNm])
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCategory(prevCategory => ({
      ...prevCategory,
      [name]: value
    }));
  };

  const handleColorChange = (color: any) => {
    setCategory(prevCategory => ({
      ...prevCategory,
      attrColor: color.hex
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    try {
      const updatedCategory = await upSertZAttribute(category);
      const newOption = {
        value: updatedCategory.attrId,
        label: updatedCategory.attrNm,
        color: showColor ? updatedCategory.attrColor : undefined
      };
      toast.success(`${(attrType || '').toTranslatedDisplay()} ${'saved successfully!'.toTranslatedDisplay()}`)
      onAddNew(newOption);
    } catch (error) {
      toast.error(`${'Error:'.toTranslatedDisplay()} ${error}`)
      console.error(`Error upserting ${attrType}:`, error);
    }
  };
  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">{`${attrType} Name`}</label>
          <input
            type="text"
            className="form-control"
            placeholder={`${label}`}
            name="attrNm"
            value={category.attrNm}
            onChange={handleChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">{`${attrType} Description`}</label>
          <input
            type="text"
            className="form-control"
            placeholder={`Description (Optional)`}
            name="attrTxt"
            value={category?.attrTxt || ''}
            onChange={handleChange}
          />
        </div>

        {showColor && (
          <div className="mb-3">
            <label className="form-label">{'Category Color'.toTranslatedDisplay()}</label>
            <CirclePicker
              color={category.attrColor}
              onChangeComplete={handleColorChange}
            />
          </div>
        )}

        <button onClick={handleSubmit} className="btn btn-primary">{'Save'.toTranslatedDisplay()}</button>
      </form>
    </div>
  );
}

interface MultiSelectPickerProps {
  categoryType: AttrType;
  label: string;
  tabIndex?: number;
  onAttributeChange: (attributeIds: string[]) => void;
  showColor?: boolean;
  selectedAttributes?: string[];
}

export function MultiSelectPicker({
  categoryType,
  label,
  tabIndex,
  onAttributeChange,
  showColor = false,
  selectedAttributes = [],
}: MultiSelectPickerProps) {
  const [attributes, setAttributes] = useState<OptionType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<OptionType[]>([]);
  
  useEffect(() => {
    const getAttributes = async () => {
      try {
        const fetchedAttributes: ZAttributes[] = await fetchZAttributes(categoryType);
        const attributesArray = Array.isArray(fetchedAttributes) ? fetchedAttributes : [fetchedAttributes];
        const options = attributesArray.map(attribute => ({
          value: attribute.attrId,
          label: attribute.attrNm,
          color: showColor ? attribute.attrColor : undefined,
        }));
        setAttributes(options);

        
      } catch (error) {
        console.error(`Error fetching ${label}:`, error);
      }
    };

    getAttributes();
  }, [categoryType, showColor, label]);

  useEffect(() => {
// Set the default selected attributes if provided
    if (selectedAttributes.length > 0) {
      const defaultOptions = attributes.filter(option => selectedAttributes.includes(option.value));
      setSelectedOptions(defaultOptions);
    }
  },[selectedAttributes,attributes])

  const customStyles = mergeSelectStyles({
    option: (provided: any, { data }: any) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
    }),
    multiValue: (provided: any, { data }: any) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
    }),
  });

  const formatOptionLabel = ({ label, color }: OptionType) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {showColor && color && (
        <span style={{ backgroundColor: color, width: 16, height: 16, display: 'inline-block', marginRight: 8 }}></span>
      )}
      {label}
    </div>
  );

  const handleSelectChange = (newValue: MultiValue<OptionType>, actionMeta: ActionMeta<OptionType>) => {
    const selectedOptions1 = newValue as OptionType[];
    const addNewOption = selectedOptions1.find(option => option.value === 'add_new');
    if (addNewOption) {
      setIsModalOpen(true);
    } else {
      setSelectedOptions(selectedOptions1);
      onAttributeChange(selectedOptions1.map(option => option.value));
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleAddNew = (newOption: OptionType) => {
    setIsModalOpen(false);
    setAttributes(prevAttributes => [...prevAttributes, newOption]);
    setSelectedOptions(prevSelected => [...prevSelected, newOption]);
    onAttributeChange([...selectedOptions.map(option => option.value), newOption.value]);
  };

  const modifiedOptions = [
    { value: 'add_new', label: 'Add New'.toTranslatedDisplay(), color: showColor ? '' : undefined },
    ...attributes,
  ];

  return (
    <div className="searchable-picker">
       {label && (
        <label className="form-label">
          {label}
          {categoryType === AttrType.Tags && (
            <i className="bi bi-tags-fill ms-2" style={{ color: '#FFC700' }}></i>
          )}
        </label>
      )}
      <Select
        isMulti
        className="react-select-styled react-select"
        classNamePrefix="react-select"
        options={modifiedOptions}
        formatOptionLabel={formatOptionLabel}
        styles={customStyles}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        placeholder={`Select ${label}`}
        value={selectedOptions}
        onChange={handleSelectChange}
        tabIndex={tabIndex}
      />
      <CustomModal
        title={`${'Add New'.toTranslatedDisplay()} ${(label || '').toTranslatedDisplay()}`}
        isVisible={isModalOpen}
        onClose={handleModalClose}
        body={<GenericForm attrType={categoryType} onAddNew={handleAddNew} showColor={showColor} label={label} />}
        id={""}
        onSave={() => {}}
      />
    </div>
  );
}

interface GenericAttrDisplayProps{
  attrId:string;
  attrType:AttrType;
}

export const GenericAttrDisplay : React.FC<GenericAttrDisplayProps> = ({ attrId, attrType }) =>{
  const [attrList,setAttrList] = useState<ZAttributes[]>([])
  useEffect(() => {
    const getAttributes = async () =>{
      const result = await fetchZAttributes(attrType)
      setAttrList(result)
    }
    getAttributes()
  },[attrType])
  return(<>
    {attrList.find(attr=>attr.attrId===attrId)?.attrNm||''}
  </>)
}



